import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { LinkClub } from '@/modules/auth/use-cases'
import { reportError } from '@/shared/infra/observability'
import { useAuth } from '@/shared/ui/hooks'

const LINK_CLUB_FAILED_MESSAGE =
  'We could not finish setting up your club. Please try the magic link again.'

export type UseMagicLinkPageProps = {
  onLinkClub: LinkClub
}

export type UseMagicLinkPageResult = {
  status: 'loading' | 'error'
  errorMessage?: string
}

const AUTH_WAIT_MS = 2000

type PendingClub = {
  orgId: string
  email: string
}

function readPendingClub(): { value: PendingClub | null; invalid: boolean } {
  const raw = window.localStorage.getItem('pendingClub')
  if (!raw) return { value: null, invalid: false }
  try {
    const parsed = JSON.parse(raw) as PendingClub
    return { value: parsed, invalid: false }
  } catch {
    return { value: null, invalid: true }
  }
}

export function useMagicLinkPage({ onLinkClub }: UseMagicLinkPageProps): UseMagicLinkPageResult {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const startedRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (startedRef.current) return

    const pending = readPendingClub()
    if (pending.invalid) {
      startedRef.current = true
      setStatus('error')
      setErrorMessage('Club setup data is invalid. Please restart.')
      return
    }

    if (!pending.value) {
      startedRef.current = true
      setStatus('error')
      setErrorMessage('Club setup data not found. Please restart the onboarding process.')
      return
    }

    const queryOrgId = searchParams.get('org_id')
    if (queryOrgId && pending.value.orgId !== queryOrgId) {
      startedRef.current = true
      setStatus('error')
      setErrorMessage('Club setup data mismatch. Please restart the onboarding process.')
      return
    }

    if (!user) {
      if (timeoutRef.current) return
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        startedRef.current = true
        setStatus('error')
        setErrorMessage('Authentication failed. Please try clicking the magic link again.')
      }, AUTH_WAIT_MS)
      return
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    startedRef.current = true
    const orgData = pending.value

    void (async () => {
      try {
        await onLinkClub({ orgId: orgData.orgId, userId: user.id, email: orgData.email })
        window.localStorage.removeItem('pendingClub')
        navigate('/dashboard', { replace: true })
      } catch (err) {
        const message = reportError(err, { fallbackMessage: LINK_CLUB_FAILED_MESSAGE })
        setStatus('error')
        setErrorMessage(message)
      }
    })()
  }, [navigate, onLinkClub, searchParams, user])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  return { status, errorMessage }
}
